import { CamelCase, ObjectValues } from '@stadionhq-v3/core';
import { lowerFirstLetter } from '@utils/strings.utils';

/**
 * Content types IDs (names) in Contentful are in camelCase. But oddly, the key __typename returned by GraphQL's Contentful is in PascalCase.
 * But, content types IDs in webhook events sent by Contentful are in camelCase. So we need both.
 *
 * CONTENT_TYPE_IDS is mostly used for everything linked to webhook like revalidation.
 * */

export const SETTINGS_CONTENT_TYPES = {
  DICTIONARY_KEY_TAG: 'DictionaryKey',
  GLOBAL_SETTINGS: 'GlobalSettings',
  SITE_SETTINGS: 'SiteSettings',
  THEME: 'Theme',
} as const;

export const CONTENT_BLOCK_CONTENT_TYPES = {
  HEADER_HERO_BLOCK: 'HeaderBlocksHero',
  ARTICLE_PROMO_BLOCK: 'ArticlePromo',
  ACCORDION_BLOCK: 'AccordionBlock',
  CONTENT_SCROLLER_BLOCK: 'ContentScrollerBlock',
  DOWNLOAD_BLOCK: 'DownloadBlock',
  GATEWAY_CAROUSEL_BLOCK: 'GatewayCarouselBlock',
  HEADER_BLOCK: 'HeaderBlock',
  PROMO_BLOCK: 'PromoBlock',
  STATS_BLOCK: 'StatsBlock',
  TABLE_BLOCK: 'TableBlock',
  TEXT_BLOCK: 'TextBlock',
  TEXT_WITH_IMAGE_BLOCK: 'TextWithImageBlock',
  EMBED_BLOCK: 'EmbedBlock',
  NEWS_CAROUSEL_BLOCK: 'NewsCarouselBlock',
  PROMO_CAROUSEL_BLOCK: 'PromoCarouselBlock',
  TABBED_CAROUSEL_BLOCK: 'TabbedCarouselBlock',
  FOOTER_BLOCK: 'FooterBlock',
  SCRIPT_EMBED: 'EmbedScriptEmbed',
  AD_SLOT: 'AdSlot',
  FIXTURES_SUMMARY_BLOCK: 'FixturesSummaryBlock',
  GAMIFICATION_ENTRY: 'GamificationEntry',
  COMET_FIXTURE_SEARCH: 'CometFixtureSearch',
} as const;

export const EMBED_CONTENT_TYPES = {
  PULL_QUOTE_EMBED: 'PullQuoteEmbed',
  SOCIAL_EMBED: 'SocialEmbed',
  SCRIPT_EMBED: 'EmbedScriptEmbed',
} as const;

export const TAG_CONTENT_TYPES = {
  ASSET_TAG: 'Asset',
  IMAGE_TAG: 'Image',
  GALLERY_TAG: 'Gallery',
  VIDEO_TAG: 'Video',
  LINK_TAG: 'Link',
  IMAGE_LINK_TAG: 'ImageLink',
  CATEGORY_TAG: 'Category',
  FIXTURE_TAG: 'Fixture',
  ACCORDION_SECTION: 'AccordionSection',
  HERO_SLIDE: 'ItemHeroSlide',
  SPONSOR_GROUP_TAG: 'SponsorGroup',
  CLUB_TAG: 'Club',
} as const;

export const FOOTBALL_CONTENT_TYPES = {
  TEAM: 'Team',
  PROFILE: 'Profile',
  COMPETITION: 'Competition',
  SEASON: 'Season',
} as const;

export const VIDEO_CONTENT_TYPES = {
  VIDEO_CAROUSEL: 'CarouselVideo',
  VIDEO: 'Video',
  VIDEO_LANDING_PAGE: 'VideoLandingPage',
} as const;

export const PAGE_CONTENT_TYPES = {
  APP_CONTENT_PAGE: 'AppContentPage',
  ARTICLE_PAGE: 'ArticlePage',
  CONTENT_PAGE: 'ContentPage',
  GALLERY_ARTICLE_PAGE: 'GalleryArticlePage',
  HOME_PAGE: 'HomePage',
  NEWS_LISTING_PAGE: 'NewsListingPage',
  VIDEO_ARTICLE_PAGE: 'VideoArticlePage',
  FIXTURE_PAGE: TAG_CONTENT_TYPES.FIXTURE_TAG,
  FIXTURE_LISTING_PAGE: 'FixtureListingPage',
  PACKAGE_PAGE: 'PagesPackage',
  CATEGORY_PAGE: TAG_CONTENT_TYPES.CATEGORY_TAG,
  TEAM_PAGE: FOOTBALL_CONTENT_TYPES.TEAM,
  PROFILE_PAGE: FOOTBALL_CONTENT_TYPES.PROFILE,
  VIDEO_LANDING_PAGE: VIDEO_CONTENT_TYPES.VIDEO_LANDING_PAGE,
} as const;

export const NAV_CONTENT_TYPES = {
  IN_PAGE_HEADER_NAVIGATION: 'InPageHeaderNavigation',
  ITEM_NAVIGATION: 'NavigationItem',
  NAVIGATION: 'Navigation',
} as const;

export const CONTENT_TYPES = {
  ...SETTINGS_CONTENT_TYPES,
  ...PAGE_CONTENT_TYPES,
  ...CONTENT_BLOCK_CONTENT_TYPES,
  ...EMBED_CONTENT_TYPES,
  ...TAG_CONTENT_TYPES,
  ...NAV_CONTENT_TYPES,
  ...FOOTBALL_CONTENT_TYPES,
  ...VIDEO_CONTENT_TYPES,
};

export const CONTENT_TYPE_IDS = {
  ...Object.fromEntries(
    Object.entries(CONTENT_TYPES).map(([key, value]) => [
      key,
      lowerFirstLetter(value),
    ])
  ),
};

export type PageContentType = ObjectValues<typeof PAGE_CONTENT_TYPES>;

export type ContentType = ObjectValues<
  typeof SETTINGS_CONTENT_TYPES &
    typeof CONTENT_BLOCK_CONTENT_TYPES &
    typeof PAGE_CONTENT_TYPES &
    typeof TAG_CONTENT_TYPES &
    typeof FOOTBALL_CONTENT_TYPES
>;

export type ContentTypeId = CamelCase<ContentType>;
